import type { Chip } from "../core/chip";
import type { TrustTagAssignment } from "../trust/trust";

const now = new Date("2026-07-18T12:00:00.000Z").toISOString();

const cpuTrust: TrustTagAssignment[] = [
  {
    tagId: "subject.deckos",
    relation: "about",
    origin: "fixture",
    confidence: 1,
    locked: true,
    reason: "Primary DeckOS subject",
  },
  {
    tagId: "state.current",
    relation: "is",
    origin: "fixture",
    confidence: 1,
    reason: "Current working CHIP",
  },
  {
    tagId: "focus.now",
    relation: "is",
    origin: "fixture",
    confidence: 1,
    reason: "Focused work item",
  },
  { tagId: "lane.core-authority", relation: "about", origin: "fixture" },
  { tagId: "invoke.build", relation: "invokes", origin: "fixture" },
  { tagId: "invoke.verify", relation: "invokes", origin: "fixture" },
  { tagId: "source.local", relation: "sourced-from", origin: "fixture" },
];

const arcopediaTrust: TrustTagAssignment[] = [
  {
    tagId: "subject.arcopolis.core",
    relation: "about",
    origin: "fixture",
    confidence: 1,
    reason: "Archive subject",
  },
  {
    tagId: "state.persistent",
    relation: "is",
    origin: "fixture",
    confidence: 1,
  },
  { tagId: "lane.public-signal", relation: "about", origin: "fixture" },
  { tagId: "source.unity", relation: "sourced-from", origin: "fixture" },
];

const loremineTrust: TrustTagAssignment[] = [];

const buildTrust: TrustTagAssignment[] = [
  { tagId: "subject.arcopolis.unity", relation: "about", origin: "fixture" },
  { tagId: "state.dormant", relation: "is", origin: "fixture" },
  { tagId: "lane.workflow-process", relation: "about", origin: "fixture" },
  { tagId: "source.codex", relation: "sourced-from", origin: "fixture" },
];

const verifyTrust: TrustTagAssignment[] = [
  { tagId: "subject.arcopolis.lore", relation: "about", origin: "fixture" },
  { tagId: "state.superseded", relation: "is", origin: "fixture" },
  { tagId: "lane.experiment-lab", relation: "about", origin: "fixture" },
  { tagId: "source.github", relation: "sourced-from", origin: "fixture" },
];

const artemisTrust: TrustTagAssignment[] = [
  {
    tagId: "subject.arcopolis.lore",
    relation: "about",
    origin: "fixture",
    confidence: 0.7,
  },
  {
    tagId: "state.review",
    relation: "is",
    origin: "fixture",
    confidence: 1,
    reason: "Needs review",
  },
  { tagId: "lane.experiment-lab", relation: "about", origin: "fixture" },
  { tagId: "invoke.artemis", relation: "invokes", origin: "fixture" },
  { tagId: "invoke.build", relation: "invokes", origin: "fixture" },
  {
    tagId: "condition.conflicting-authority",
    relation: "blocks",
    origin: "fixture",
    reason: "Protected authority dispute",
  },
  { tagId: "risk.review", relation: "blocks", origin: "fixture" },
];

export const demoSector47: Chip[] = [
  {
    id: "cpu-001",
    title: "CPU-001",
    type: "SYSTEM",
    state: "ACTIVE",
    summary: "Game Two CPU encounter and central DeckOS work capsule.",
    goal: "Resolve visual identity and seed the first vertical slice.",
    known: [
      "Sector 47 is the active capsule.",
      "Demo routes are legal and local.",
    ],
    open: [
      "Bind richer data sources.",
      "Ship Android packaging after the web slice stabilizes.",
    ],
    imprints: [
      "Arcopolis first.",
      "Current main preserves approved project state.",
    ],
    routes: [
      {
        id: "arcopedia",
        kind: "DESTINATION",
        label: "ARCOPEDIA",
        targetChipId: "arcopedia",
        invoke: "ARCOPEDIA",
        state: "RECOMMENDED",
        reason: "Primary reference routes should stay prominent.",
      },
      {
        id: "loremine",
        kind: "DESTINATION",
        label: "LOREMINE",
        targetChipId: "loremine",
        invoke: "LOREMINE",
        state: "AVAILABLE",
      },
      {
        id: "artemis",
        kind: "DESTINATION",
        label: "ARTEMIS",
        targetChipId: "artemis",
        invoke: "ARTEMIS",
        state: "EXPLORATORY",
      },
      {
        id: "build",
        kind: "PROCEDURE",
        label: "BUILD",
        invoke: "BUILD",
        state: "AVAILABLE",
      },
      {
        id: "verify",
        kind: "PROCEDURE",
        label: "VERIFY",
        invoke: "VERIFY",
        state: "AVAILABLE",
      },
    ],
    truth: "NONE",
    risk: "CLEAR",
    sourceRefs: [
      "DECKOS_0_1_BETA_CODEX_BUILD_PROMPT.md",
      "DECKOS_0_1_BETA_FINAL_IMPLEMENTATION_SPEC.md",
      "14021.png",
    ],
    revision: 12,
    createdAt: now,
    updatedAt: now,
    trust: cpuTrust,
  },
  {
    id: "arcopedia",
    title: "ARCOPEDIA",
    type: "DOCUMENT",
    state: "AVAILABLE",
    summary:
      "Reference route for approved build language and shared definitions.",
    known: ["Routes should expose legal next actions."],
    open: ["Import canonical entries later."],
    imprints: ["Recommended route."],
    routes: [],
    truth: "NONE",
    risk: "CLEAR",
    sourceRefs: [],
    revision: 3,
    createdAt: now,
    updatedAt: now,
    trust: arcopediaTrust,
  },
  {
    id: "loremine",
    title: "LOREMINE",
    type: "DOCUMENT",
    state: "AVAILABLE",
    summary: "Local content mine for fast demo data and future authored notes.",
    known: ["Demo fixtures are allowed."],
    open: ["Swap to persisted content later."],
    imprints: [],
    routes: [],
    truth: "NONE",
    risk: "CLEAR",
    sourceRefs: [],
    revision: 1,
    createdAt: now,
    updatedAt: now,
    trust: loremineTrust,
  },
  {
    id: "build",
    title: "BUILD",
    type: "PROCEDURE",
    state: "AVAILABLE",
    summary: "Smallest safe procedure for working repository deltas.",
    known: ["Code changes are DELTAs.", "Build must not mutate TRUTH."],
    open: ["Add deeper repository context."],
    imprints: [],
    routes: [],
    truth: "NONE",
    risk: "CLEAR",
    sourceRefs: [],
    revision: 1,
    createdAt: now,
    updatedAt: now,
    trust: buildTrust,
  },
  {
    id: "verify",
    title: "VERIFY",
    type: "PROCEDURE",
    state: "AVAILABLE",
    summary: "Validation route for format, lint, typecheck, tests, and build.",
    known: ["Checks prove the slice before handoff."],
    open: ["Add richer CI wiring."],
    imprints: [],
    routes: [],
    truth: "NONE",
    risk: "CLEAR",
    sourceRefs: [],
    revision: 1,
    createdAt: now,
    updatedAt: now,
    trust: verifyTrust,
  },
  {
    id: "artemis",
    title: "ARTEMIS",
    type: "DOCUMENT",
    state: "EXPLORATORY",
    summary: "Creative route for visual and interaction references.",
    known: ["Approved visual intent lives in 14021.png."],
    open: ["Expand into an image route later."],
    imprints: [],
    routes: [],
    truth: "NONE",
    risk: "REVIEW",
    sourceRefs: [],
    revision: 1,
    createdAt: now,
    updatedAt: now,
    trust: artemisTrust,
  },
];
